import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api, loadBuildingsForSelector } from '../../../api';
import { useAuth } from '../../../contexts/AuthContext';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../../utils/buildingSelection';
import { incomeExpenseApi, ApartmentSurplusAccount } from '../../../api/incomeExpenseApi';
import { printVoucherHtml } from '../../../utils/voucherPrint';

const APARTMENT_SURPLUS_PAGE_SIZE = 25;

const formatCurrency = (n: number) => `${new Intl.NumberFormat('vi-VN').format(Math.round(n))}đ`;

const ONLINE_SURPLUS_LABEL = 'Nhận tiền từ chuyển khoản online';

const LedgerVoucherModal: React.FC<{
  entryId: string;
  title: string;
  onClose: () => void;
}> = ({ entryId, title, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setFailed(false);
      setHtmlContent('');
      try {
        const html = await incomeExpenseApi.fetchLedgerVoucherHtml(entryId);
        if (!cancelled) setHtmlContent(html);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [entryId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => htmlContent && printVoucherHtml(htmlContent)}
              disabled={!htmlContent}
              className="text-sm text-cyan-600 hover:text-cyan-700 disabled:text-gray-400"
            >
              In
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl leading-none px-2"
              aria-label="Đóng"
            >
              ×
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-gray-100 relative" style={{ minHeight: '70vh' }}>
          {loading && !failed && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
              <span className="ml-2 text-gray-600 text-sm">Đang tải...</span>
            </div>
          )}
          {failed ? (
            <div className="flex items-center justify-center py-16 text-gray-500 text-sm">
              Không thể tải phiếu
            </div>
          ) : htmlContent ? (
            <iframe
              title={title}
              srcDoc={htmlContent}
              className="w-full border-0 bg-white"
              style={{ minHeight: '70vh', display: 'block' }}
              sandbox="allow-same-origin allow-scripts"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

const resolveVoucherTitle = (displayLabel?: string | null, entryType?: string) => {
  if (displayLabel === ONLINE_SURPLUS_LABEL) return ONLINE_SURPLUS_LABEL;
  if (entryType === 'SURPLUS_OFFSET') return 'Cấn trừ phiếu thu';
  if (entryType === 'SURPLUS_CREDIT') return 'Đóng dư phiếu thu';
  return 'Xem phiếu';
};

export const ApartmentSurplusAccountsTab: React.FC = () => {
  const { user } = useAuth();
  const [buildings, setBuildings] = useState<Array<{ id: string; name: string }>>([]);
  const [buildingId, setBuildingId] = useState('');
  const [search, setSearch] = useState('');
  const [accounts, setAccounts] = useState<ApartmentSurplusAccount[]>([]);
  const [totalBalance, setTotalBalance] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<ApartmentSurplusAccount | null>(null);
  const [voucherPreview, setVoucherPreview] = useState<{ entryId: string; title: string } | null>(null);

  useEffect(() => {
    loadBuildingsForSelector().then((data) => {
      const list = data || [];
      setBuildings(list);
      setBuildingId((prev) => applyDefaultBuildingSelection(list, user, prev));
    });
  }, [user]);

  useEffect(() => {
    setPage(1);
  }, [buildingId, search]);

  const loadAccounts = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const data = await incomeExpenseApi.listApartmentSurplusAccounts(buildingId, search, {
        page,
        limit: APARTMENT_SURPLUS_PAGE_SIZE,
      });
      setAccounts(data.items || []);
      setTotalBalance(data.summary?.totalBalance ?? 0);
      setRowCount(data.summary?.rowCount ?? 0);
      setTotalPages(data.summary?.totalPages ?? 0);
    } catch {
      toast.error('Không thể tải tài khoản căn hộ');
      setAccounts([]);
      setTotalBalance(0);
      setRowCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [buildingId, search, page]);

  useEffect(() => {
    const timer = setTimeout(loadAccounts, 300);
    return () => clearTimeout(timer);
  }, [loadAccounts]);

  const pageStart = rowCount === 0 ? 0 : (page - 1) * APARTMENT_SURPLUS_PAGE_SIZE + 1;
  const pageEnd = rowCount === 0 ? 0 : Math.min(page * APARTMENT_SURPLUS_PAGE_SIZE, rowCount);

  const openDetail = async (apartmentId: string) => {
    try {
      setDetail(await incomeExpenseApi.getApartmentSurplusAccount(apartmentId));
    } catch {
      toast.error('Chưa có tài khoản căn hộ cho căn hộ này');
    }
  };

  const openDetailBySearchCode = async () => {
    const code = search.trim();
    if (!buildingId || !code) return;
    try {
      setDetail(await incomeExpenseApi.getApartmentSurplusAccountByCode(buildingId, code));
    } catch {
      toast.error(`Không tìm thấy căn hộ "${code}" hoặc không có quyền xem`);
    }
  };

  if (detail) {
    return (
      <div className="space-y-4">
        <button type="button" onClick={() => setDetail(null)} className="text-cyan-600 text-sm">← Quay lại</button>
        <div className="bg-white border rounded-lg p-4">
          <h3 className="text-lg font-semibold">Chi tiết thu chi - {detail.apartment?.code}</h3>
          <p className="text-sm text-gray-600">{detail.packageName} · Số dư: {formatCurrency(detail.balance)}</p>
        </div>
        <div className="bg-white border rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left">Ngày</th>
                <th className="px-4 py-2 text-left">Diễn giải / Số bút toán</th>
                <th className="px-4 py-2 text-left">Loại</th>
                <th className="px-4 py-2 text-right text-blue-600">Thu</th>
                <th className="px-4 py-2 text-right text-red-600">Chi</th>
                <th className="px-4 py-2 text-center">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {(detail.ledgerEntries || []).map((entry) => (
                <tr key={entry.id} className="border-t">
                  <td className="px-4 py-2">{new Date(entry.actualDate).toLocaleString('vi-VN')}</td>
                  <td className="px-4 py-2">
                    <div className="leading-snug">
                      <div>{entry.displayLabel || entry.description || '—'}</div>
                      {entry.voucherCode && (
                        <div className="font-medium text-gray-900">
                          {entry.voucherCode}
                          {(entry.displayLabel || '').includes('công nợ nhận bàn giao') && (
                            <span className="block text-[10px] font-normal text-gray-500">
                              Số phiếu nhập (PTNB) — không phải phiếu thu phí PTDA
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2">{entry.revenueCategory?.name || entry.expenditureCategory?.name || '—'}</td>
                  <td className="px-4 py-2 text-right text-blue-600">
                    {formatCurrency(
                      entry.ledgerCreditAmount ??
                        (entry.entryType === 'SURPLUS_CREDIT' ? entry.amount : 0),
                    )}
                  </td>
                  <td className="px-4 py-2 text-right text-red-600">
                    {formatCurrency(
                      entry.ledgerDebitAmount ??
                        (entry.entryType === 'SURPLUS_OFFSET' ? entry.amount : 0),
                    )}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {entry.canViewVoucher && entry.voucherViewEntryId ? (
                      <button
                        type="button"
                        onClick={() =>
                          setVoucherPreview({
                            entryId: entry.voucherViewEntryId!,
                            title: resolveVoucherTitle(entry.displayLabel, entry.entryType),
                          })
                        }
                        className="text-cyan-600 hover:text-cyan-800 text-sm font-medium"
                      >
                        Xem
                      </button>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {voucherPreview && (
          <LedgerVoucherModal
            entryId={voucherPreview.entryId}
            title={voucherPreview.title}
            onClose={() => setVoucherPreview(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {shouldShowBuildingSelector(buildings) && (
        <select value={buildingId} onChange={(e) => selectBuilding(user?.id, e.target.value, setBuildingId)} disabled={!needsBuildingSelection(buildings)} className="rounded-lg border px-3 py-2 text-sm">
          {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        )}
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Tìm mã/tên căn hộ (kể cả số dư 0)"
          className="rounded-lg border px-3 py-2 text-sm min-w-[240px]"
        />
        {search.trim() && (
          <button
            type="button"
            onClick={openDetailBySearchCode}
            className="rounded-lg border border-cyan-600 px-3 py-2 text-sm text-cyan-700 hover:bg-cyan-50"
          >
            Mở lịch sử căn {search.trim()}
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500">
        Danh sách mặc định chỉ căn có số dư &gt; 0. Gõ mã căn (vd. A0107) để xem cả số dư 0 và lịch sử PTNB.
        Thu dư nhập công nợ (bàn giao) không hiện ở Hoạt động thu — chỉ ở đây và Thống kê II.
      </p>

      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">Mã căn hộ</th>
              <th className="px-4 py-3 text-left">Tên gói</th>
              <th className="px-4 py-3 text-left">Loại</th>
              <th className="px-4 py-3 text-right">Số dư</th>
              <th className="px-4 py-3 text-left">Người tạo</th>
              <th className="px-4 py-3 text-left">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">Đang tải...</td></tr>
            ) : accounts.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                  {search.trim() ? (
                    <span>
                      Không có căn số dư &gt; 0 khớp tìm kiếm.{' '}
                      <button
                        type="button"
                        onClick={openDetailBySearchCode}
                        className="text-cyan-700 underline hover:text-cyan-900"
                      >
                        Mở lịch sử căn {search.trim()}
                      </button>
                    </span>
                  ) : (
                    'Chưa có tài khoản căn hộ có số dư'
                  )}
                </td>
              </tr>
            ) : (
              accounts.map((account) => (
                <tr key={account.id} className="border-t">
                  <td className="px-4 py-3 font-medium">{account.apartment?.code}</td>
                  <td className="px-4 py-3">{account.packageName}</td>
                  <td className="px-4 py-3">{account.accountType}</td>
                  <td className="px-4 py-3 text-right font-semibold">{formatCurrency(account.balance)}</td>
                  <td className="px-4 py-3">{account.creator?.name || '—'}</td>
                  <td className="px-4 py-3">
                    <button type="button" onClick={() => openDetail(account.apartmentId)} className="text-cyan-600 mr-2">👁</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {!loading && accounts.length > 0 && (
            <tfoot className="bg-gray-50 border-t-2 border-gray-200 font-semibold">
              <tr>
                <td className="px-4 py-3" colSpan={3}>Tổng cộng</td>
                <td className="px-4 py-3 text-right">{formatCurrency(totalBalance)}</td>
                <td className="px-4 py-3" colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-500 space-y-3">
          <div>Tổng {rowCount} tài khoản</div>
          {rowCount > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-gray-700">
              <div>
                Đang xem {pageStart} đến {pageEnd} trong tổng số {rowCount} bản ghi
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={page === 1 || loading}
                    className="px-3 py-1 bg-white border border-gray-200 rounded disabled:opacity-50"
                  >
                    ←
                  </button>
                  <span className="text-xs text-gray-600">
                    Trang {page} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={page >= totalPages || loading}
                    className="px-3 py-1 bg-white border border-gray-200 rounded disabled:opacity-50"
                  >
                    →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApartmentSurplusAccountsTab;
